// src/store/slices/conversationsSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { getAllConversations, assignConversation, deleteChat, sendMessageToLead, bulkConversationAction, BulkActionPayload } from '../../services/whatsapp';
import moment from 'moment';
import { RootState } from '../store';
import logger from '../../utils/logger';

// Types
export interface User {
  id: string;
  _id: string;
  name: string;
  mobile: string;
  email?: string;
  userIcon: string;
  msg: string;
  timeStamp: string;
  msgStatus: string;
  lastMessageAt: string;
  lead: string;
  label?: Array<string>;
  status?: Array<string>;
  assignedTo: Array<any>;
  convoId: string;
  unReadCount?: number;
  isBlocked?: boolean; // Track if contact is blocked
  // Add the lastMessage property to the User interface
  lastMessage?: {
    _id?: string;
    messageDelivery?: string;
    messageType?: 'text' | 'image' | 'video' | 'document' | 'audio' | 'reaction' | 'template';
    messageId?: string;
    message?: {
      message_id?: string;
      emoji?: string;
      body?: string | any;
      filename?: string;
      mime_type?: string;
      [key: string]: any;
    };
    status?: string;
    sender?: any;
    createdAt?: string;
    [key: string]: any;
  };
}

export interface ConversationGroup {
  title: string; // Date
  data: User[];
}

export interface ConversationsState {
  groups: ConversationGroup[];
  selectedConversation: User | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  totalConversations: number;
  currentPage: number;
  filters: {
    search?: string;
    isRead?: boolean;
    isPinned?: boolean;
    isAssigned?: boolean;
    isAssignedToMe?: boolean;
    isAutomation?: boolean;
    isInCampaignInbox?: boolean;
    dateRangeFor?: 'firstMessage' | 'lastMessage';
    startDate?: string;
    endDate?: string;
    channel?: string;
    leadIds?: string[];
    leadStatus?: string[];  // Array of status values
    leadLabel?: string[];   // Array of label values
  };
  // Bulk selection state
  selectedConversationIds: string[];
  isSelectionMode: boolean;
  bulkActionStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  bulkActionError: string | null;
}

// Initial state
const initialState: ConversationsState = {
  groups: [],
  selectedConversation: null,
  status: 'idle',
  error: null,
  totalConversations: 0,
  currentPage: 1,
  filters: {},
  // Bulk selection initial state
  selectedConversationIds: [],
  isSelectionMode: false,
  bulkActionStatus: 'idle',
  bulkActionError: null
};

// Helper function
const getDate = (timestamp: string) => timestamp?.split('T')[0];

// Thunks
export const fetchConversations = createAsyncThunk(
  'conversations/fetchConversations',
  async ({ page, refresh = false }: { page: number, refresh?: boolean }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { filters } = state.whatsapp.conversations;
      
      // Base pagination data
      const data = {
        orderBy: "_id",
        isAscending: false,
        page,
        perPage: 15,
      };
      
      // Build params from filters
      const params: any = {
        page,
        perPage: 15,
        isInInbox: filters.isAutomation ? true : false,
      };
      
      // Add any active filters
      if (filters.search) params.search = filters.search;
      if (filters.isRead === false) params.isRead = false;
      if (filters.isPinned === true) params.isPinned = true;
      if (filters.isAssigned === false) params.isAssigned = false;
      if (filters.isAssignedToMe === true) params.isAssignedToMe = true;
      if (filters.isInCampaignInbox === true) params.isInCampaignInbox = true;
      if (filters.dateRangeFor) params.dateRangeFor = filters.dateRangeFor;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.channel) params.channel = filters.channel;
      if (filters.leadIds && filters.leadIds.length > 0) {
        params.lead = filters.leadIds.join(',');
      }
      // Add leadStatus and leadLabel filters
      if (filters.leadStatus && filters.leadStatus.length > 0) {
        params.leadStatus = filters.leadStatus.join(',');
      }
      if (filters.leadLabel && filters.leadLabel.length > 0) {
        params.leadLabel = filters.leadLabel.join(',');
      }

      logger.log("Filters object:", filters);
logger.log("API parameters being sent:", params);

      const response = await getAllConversations(data, params);
      
      return {
        data: response.data.data,
        total: response.data.total,
        page,
        refresh
      };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch conversations');
    }
  }
);

export const assignConversationToUser = createAsyncThunk(
  'conversations/assignConversation',
  async ({ conversationId, userId }: { conversationId: string, userId: string }, { rejectWithValue }) => {
    try {
      const data = {
        assignToIds: [userId],
      };
      
      const response = await assignConversation(conversationId, data);
      return {
        conversationId,
        assignedUser: response.data?.data.assignToUsers?.[0]
      };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to assign conversation');
    }
  }
);

export const deleteConversation = createAsyncThunk(
  'conversations/deleteConversation',
  async (conversationId: string, { rejectWithValue }) => {
    try {
      await deleteChat(conversationId);
      return conversationId;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete conversation');
    }
  }
);

export const startNewChat = createAsyncThunk(
  'conversations/startNewChat',
  async ({ phone, message = "Hello!" }: { phone: string, message?: string }, { rejectWithValue }) => {
    try {
      const payload = {
        phone,
        type: "text",
        phoneId: "190076364199723", // This should come from user settings
        text: {
          body: message,
        },
      };

      await sendMessageToLead(payload);

      // After sending message, we need to fetch conversations to get the new chat
      return { success: true };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to start new chat');
    }
  }
);

// Bulk action thunk
export const performBulkAction = createAsyncThunk(
  'conversations/performBulkAction',
  async (payload: BulkActionPayload, { rejectWithValue }) => {
    try {
      logger.log('Performing bulk action:', payload);
      const response = await bulkConversationAction(payload);
      return {
        action: payload.action,
        conversationIds: payload.conversationIds || [],
        response: response.data
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to perform bulk action');
    }
  }
);

// Helper to get all loaded conversation IDs from state
const getAllLoadedIds = (groups: ConversationGroup[]): string[] => {
  const ids: string[] = [];
  groups.forEach(group => {
    group.data.forEach(conversation => {
      ids.push(conversation._id);
    });
  });
  return ids;
};

// Select next N conversations thunk (fetches more pages until we have enough)
export const selectNextConversations = createAsyncThunk(
  'conversations/selectNextConversations',
  async (count: number, { getState, dispatch, rejectWithValue }) => {
    try {
      let state = getState() as RootState;
      let { groups, selectedConversationIds, totalConversations, currentPage } = state.whatsapp.conversations;

      // Keep fetching until we have enough unselected conversations or no more to fetch
      let allLoadedIds = getAllLoadedIds(groups);
      let unselectedIds = allLoadedIds.filter(id => !selectedConversationIds.includes(id));

      // Safety: Track iterations and previous count to prevent infinite loops
      let iterations = 0;
      const MAX_ITERATIONS = 20; // Safety limit
      let previousLoadedCount = -1;

      // Keep loading more pages until we have enough
      while (unselectedIds.length < count && allLoadedIds.length < totalConversations) {
        // Safety check 1: Max iterations
        iterations++;
        if (iterations > MAX_ITERATIONS) {
          logger.log('Max iterations reached, stopping fetch loop');
          break;
        }

        // Safety check 2: No progress (same count as before means API is stuck)
        if (allLoadedIds.length === previousLoadedCount) {
          logger.log('No new conversations loaded, stopping fetch loop');
          break;
        }
        previousLoadedCount = allLoadedIds.length;

        const nextPage = currentPage + 1;
        logger.log('Fetching more conversations for bulk selection, page:', nextPage);

        // Fetch next page
        await dispatch(fetchConversations({ page: nextPage })).unwrap();

        // Get updated state
        state = getState() as RootState;
        groups = state.whatsapp.conversations.groups;
        currentPage = state.whatsapp.conversations.currentPage;
        // Re-read selectedConversationIds in case it changed
        selectedConversationIds = state.whatsapp.conversations.selectedConversationIds;

        // Recalculate
        allLoadedIds = getAllLoadedIds(groups);
        unselectedIds = allLoadedIds.filter(id => !selectedConversationIds.includes(id));

        logger.log(`Loaded ${allLoadedIds.length}/${totalConversations}, unselected: ${unselectedIds.length}`);
      }

      // Select up to 'count' unselected conversations
      const idsToSelect = unselectedIds.slice(0, count);
      logger.log(`Selecting ${idsToSelect.length} conversations`);

      return { idsToSelect };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to select conversations');
    }
  }
);

// Select ALL conversations (fetches all pages)
export const selectAllConversationsAsync = createAsyncThunk(
  'conversations/selectAllConversationsAsync',
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      let state = getState() as RootState;
      let { groups, totalConversations, currentPage } = state.whatsapp.conversations;

      // Keep fetching until all conversations are loaded
      let allLoadedIds = getAllLoadedIds(groups);

      while (allLoadedIds.length < totalConversations) {
        const nextPage = currentPage + 1;
        logger.log(`Fetching all conversations, page ${nextPage}...`);

        await dispatch(fetchConversations({ page: nextPage })).unwrap();

        // Get updated state
        state = getState() as RootState;
        groups = state.whatsapp.conversations.groups;
        currentPage = state.whatsapp.conversations.currentPage;
        allLoadedIds = getAllLoadedIds(groups);

        logger.log(`Loaded ${allLoadedIds.length}/${totalConversations}`);
      }

      logger.log(`Selecting all ${allLoadedIds.length} conversations`);
      return { idsToSelect: allLoadedIds };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to select all conversations');
    }
  }
);

// Create the slice
const conversationsSlice = createSlice({
  name: 'conversations',
  initialState,
  reducers: {
    setSelectedConversation: (state, action: PayloadAction<User | null>) => {
      state.selectedConversation = action.payload;
    },
    
    clearSelectedConversation: (state) => {
      state.selectedConversation = null;
    },
    
   setFilter: (state, action: PayloadAction<{ key: keyof ConversationsState['filters'], value: any }>) => {
  const { key, value } = action.payload;
  
  // ✅ ENHANCED: Always reset pagination when any filter changes
  state.currentPage = 1;
  
  // ✅ ENHANCED: Clear groups when starting new search
  if (key === 'search') {
    state.groups = [];
    state.totalConversations = 0;
  }
  
  if (value === undefined || value === '' || value === null) {
    // ✅ Remove filter if value is empty/null/undefined
    const { [key]: _, ...remaining } = state.filters;
    state.filters = remaining;
  } else {
    // ✅ Add/update filter
    state.filters = { ...state.filters, [key]: value };
  }
},
    
  toggleFilter: (state, action: PayloadAction<keyof ConversationsState['filters']>) => {
  const key = action.payload;
  
  // ✅ ENHANCED: Reset pagination and clear data for new filter
  state.currentPage = 1;
  state.groups = [];
  state.totalConversations = 0;
  
  // ✅ Check if the filter exists, regardless of its value
  if (key in state.filters) {
    // Remove filter if it exists
    const { [key]: _, ...remaining } = state.filters;
    state.filters = remaining;
  } else {
    // ✅ For most filters, the active value is true, but there are exceptions
    let value;
    
    // Special cases for filter values
    if (key === 'isRead' || key === 'isAssigned') {
      // "Unread" and "Unassigned" filters should set false
      value = false;
    } else {
      // All other filters set true when active
      value = true;
    }
    
    state.filters = { 
      ...state.filters, 
      [key]: value
    };
  }
},
    
    clearFilters: (state) => {
      state.filters = {};
      state.currentPage = 1;
    },
    
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    
    // ✅ NEW: Add reset pagination action
resetPagination: (state) => {
  state.currentPage = 1;
  state.groups = [];
  state.totalConversations = 0;
},

    updateLastMessage: (state, action: PayloadAction<{
      conversationId: string,
      message: any
    }>) => {
      const { conversationId, message } = action.payload;

      // Update in grouped data
      state.groups = state.groups.map(group => {
        const updatedData = group.data.map(conversation => {
          if (conversation.convoId === conversationId) {
            return {
              ...conversation,
              lastMessageAt: message.createdAt,
              msg: typeof message.message.body === 'object'
                ? (message.message.body?.text?.body || '')
                : message.message.body,
              timeStamp: moment(message.createdAt).format('LT')
            };
          }
          return conversation;
        });

        return {
          ...group,
          data: updatedData
        };
      });

      // Also update in selected conversation if it matches
      if (state.selectedConversation?.convoId === conversationId) {
        state.selectedConversation = {
          ...state.selectedConversation,
          lastMessageAt: message.createdAt,
          msg: typeof message.message.body === 'object'
            ? (message.message.body?.text?.body || '')
            : message.message.body,
          timeStamp: moment(message.createdAt).format('LT')
        };
      }
    },

    // ===== BULK SELECTION REDUCERS =====

    // Toggle selection mode on/off
    toggleSelectionMode: (state) => {
      state.isSelectionMode = !state.isSelectionMode;
      // Clear selections when exiting selection mode
      if (!state.isSelectionMode) {
        state.selectedConversationIds = [];
      }
    },

    // Set selection mode explicitly
    setSelectionMode: (state, action: PayloadAction<boolean>) => {
      state.isSelectionMode = action.payload;
      if (!action.payload) {
        state.selectedConversationIds = [];
      }
    },

    // Toggle a single conversation selection
    toggleConversationSelection: (state, action: PayloadAction<string>) => {
      const conversationId = action.payload;
      const index = state.selectedConversationIds.indexOf(conversationId);

      if (index === -1) {
        // Add to selection
        state.selectedConversationIds.push(conversationId);
      } else {
        // Remove from selection
        state.selectedConversationIds.splice(index, 1);
      }

      // Auto-enable selection mode if selecting items
      if (state.selectedConversationIds.length > 0) {
        state.isSelectionMode = true;
      }
    },

    // Select multiple conversations
    selectConversations: (state, action: PayloadAction<string[]>) => {
      const newIds = action.payload.filter(
        id => !state.selectedConversationIds.includes(id)
      );
      state.selectedConversationIds.push(...newIds);
      if (state.selectedConversationIds.length > 0) {
        state.isSelectionMode = true;
      }
    },

    // Select all visible conversations
    selectAllConversations: (state) => {
      const allIds: string[] = [];
      state.groups.forEach(group => {
        group.data.forEach(conversation => {
          if (!allIds.includes(conversation._id)) {
            allIds.push(conversation._id);
          }
        });
      });
      state.selectedConversationIds = allIds;
      state.isSelectionMode = true;
    },

    // Clear all selections
    clearConversationSelection: (state) => {
      state.selectedConversationIds = [];
      state.isSelectionMode = false;
    },

    // Clear bulk action error
    clearBulkActionError: (state) => {
      state.bulkActionError = null;
      state.bulkActionStatus = 'idle';
    }
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchConversations
      .addCase(fetchConversations.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        state.status = 'succeeded';
        
        const { data, total, page, refresh } = action.payload;
        state.totalConversations = total;
        
        // Process the conversation data
        const inboxList = data;
        
        // Create properly typed groups
        const groupedData: { [date: string]: User[] } = {};
        
        // Group conversations by date
        inboxList.forEach((item: any) => {
          const date = getDate(item.lastMessageAt);
          if (!groupedData[date]) {
            groupedData[date] = [];
          }
          
          // Get the appropriate message preview based on message type
          let messagePreview = '';
          const messageType = item?.lastMessage?.messageType || 'text';
          
          // Handle different message types for the preview
          switch(messageType) {
            case 'reaction':
              // For reactions, use the emoji
              const emoji = item?.lastMessage?.message?.emoji;
              messagePreview = emoji ? `Reacted with ${emoji}` : 'Reacted to a message';
              break;
              
            case 'image':
              messagePreview = '📷 Photo';
              break;
              
            case 'video':
              messagePreview = '🎥 Video';
              break;
              
            case 'document':
              // For documents, use the filename if available
              const filename = item?.lastMessage?.message?.filename || 'Document';
              messagePreview = `📄 ${filename}`;
              break;
              
            case 'audio':
              messagePreview = '🔊 Audio';
              break;
              
            case 'template':
              // Try to extract template content
              messagePreview = item?.lastMessage?.message?.body?.text?.body || 'Template message';
              break;
              
            case 'text':
            default:
              // For text messages, handle different structures
              if (item?.lastMessage?.message?.body) {
                messagePreview = typeof item.lastMessage.message.body === 'string' 
                  ? item.lastMessage.message.body 
                  : (item.lastMessage.message.body?.text?.body || 'Message');
              } else {
                messagePreview = 'Message';
              }
              break;
          }
          
          // Transform each conversation item with proper typing
          groupedData[date].push({
            id: item?._id,
            _id: item?._id,
            name: item?.lead?.name,
            mobile: item?.lead?.phone,
            lastMessageAt: item?.lastMessageAt,
            email: item?.lead?.email,
            userIcon: `${localStorage.getItem('bucketUrl')}${item?.profile?.filePath}`,
            msg: messagePreview, // Use our processed message preview
            lead: item?.lead?._id,
            assignedTo: item?.assignedTo || [],
            timeStamp: moment(item?.lastMessage?.createdAt).format('LT'),
            msgStatus: item?.lastMessage?.status,
            convoId: item?.conversationId,
            unReadCount: item?.unReadCount,
            label: item?.lead?.label || [],
            status: item?.lead?.status || [],
            isBlocked: item?.isBlocked || item?.lead?.isBlocked || false, // Map blocked status from API
            // Store the full lastMessage for reference
            lastMessage: item?.lastMessage
          });
        });
        
        // Convert to array of groups with proper typing
        const groupedArr: ConversationGroup[] = Object.entries(groupedData).map(
          ([title, data]) => ({ title, data })
        );
        
        // If refreshing or page 1, replace data, otherwise append
        if (refresh || page === 1) {
          state.groups = groupedArr;
        } else {
          // Append new groups or merge with existing groups
          const existingTitles = new Set(state.groups.map(g => g.title));
          
          groupedArr.forEach((newGroup) => {
            if (existingTitles.has(newGroup.title)) {
              // Merge with existing group
              const existingGroup = state.groups.find(g => g.title === newGroup.title);
              if (existingGroup) {
                // Add unique conversations by id
                const existingIds = new Set(existingGroup.data.map(c => c.id));
                newGroup.data.forEach((conversation) => {
                  if (!existingIds.has(conversation.id)) {
                    existingGroup.data.push(conversation);
                  }
                });
              }
            } else {
              // Add new group
              state.groups.push(newGroup);
            }
          });
        }
        
        // Sort groups by date (newest first)
        state.groups.sort((a, b) => {
          return new Date(b.title).getTime() - new Date(a.title).getTime();
        });
        
        // Select first conversation if none selected and we have data
        if (!state.selectedConversation && state.groups.length > 0 && state.groups[0].data.length > 0) {
          state.selectedConversation = state.groups[0].data[0];
        }
      })
      .addCase(fetchConversations.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
      })
      
      // Handle assignConversationToUser
      .addCase(assignConversationToUser.fulfilled, (state, action) => {
        const { conversationId, assignedUser } = action.payload;
        
        // Update in grouped data
        state.groups = state.groups.map(group => {
          const updatedData = group.data.map(conversation => {
            if (conversation._id === conversationId) {
              return {
                ...conversation,
                assignedTo: [...conversation.assignedTo, assignedUser]
              };
            }
            return conversation;
          });
          
          return {
            ...group,
            data: updatedData
          };
        });
        
        // Also update in selected conversation if it matches
        if (state.selectedConversation?._id === conversationId) {
          state.selectedConversation = {
            ...state.selectedConversation,
            assignedTo: [...state.selectedConversation.assignedTo, assignedUser]
          };
        }
      })
      
      // Handle deleteConversation
      .addCase(deleteConversation.fulfilled, (state, action) => {
        const conversationId = action.payload;
        
        // Remove from groups
        state.groups = state.groups.map(group => ({
          ...group,
          data: group.data.filter(conversation => conversation._id !== conversationId)
        })).filter(group => group.data.length > 0); // Remove empty groups
        
        // Clear selected conversation if it's the deleted one
        if (state.selectedConversation?._id === conversationId) {
          state.selectedConversation = null;
          
          // Select first conversation if available
          if (state.groups.length > 0 && state.groups[0].data.length > 0) {
            state.selectedConversation = state.groups[0].data[0];
          }
        }
      })
      
      // Handle startNewChat
      .addCase(startNewChat.fulfilled, (state) => {
        // We don't update state here - we'll fetch conversations to get the new chat
      })

      // Handle performBulkAction
      .addCase(performBulkAction.pending, (state) => {
        state.bulkActionStatus = 'loading';
        state.bulkActionError = null;
      })
      .addCase(performBulkAction.fulfilled, (state, action) => {
        state.bulkActionStatus = 'succeeded';
        const { action: bulkAction, conversationIds } = action.payload;

        // Handle different bulk actions
        if (bulkAction === 'delete' && conversationIds.length > 0) {
          // Remove deleted conversations from groups
          state.groups = state.groups.map(group => ({
            ...group,
            data: group.data.filter(conversation =>
              !conversationIds.includes(conversation._id)
            )
          })).filter(group => group.data.length > 0);

          // Clear selected conversation if it was deleted
          if (state.selectedConversation && conversationIds.includes(state.selectedConversation._id)) {
            state.selectedConversation = null;
            // Select first conversation if available
            if (state.groups.length > 0 && state.groups[0].data.length > 0) {
              state.selectedConversation = state.groups[0].data[0];
            }
          }
        }

        // Clear selections after successful bulk action
        state.selectedConversationIds = [];
        state.isSelectionMode = false;
      })
      .addCase(performBulkAction.rejected, (state, action) => {
        state.bulkActionStatus = 'failed';
        state.bulkActionError = action.payload as string;
      })

      // Handle selectNextConversations
      .addCase(selectNextConversations.pending, (state) => {
        state.bulkActionStatus = 'loading';
      })
      .addCase(selectNextConversations.fulfilled, (state, action) => {
        const { idsToSelect } = action.payload;
        // Add new IDs to selection (avoiding duplicates)
        const newIds = idsToSelect.filter(
          (id: string) => !state.selectedConversationIds.includes(id)
        );
        state.selectedConversationIds.push(...newIds);
        state.isSelectionMode = true;
        state.bulkActionStatus = 'idle';
      })
      .addCase(selectNextConversations.rejected, (state, action) => {
        state.bulkActionStatus = 'failed';
        state.bulkActionError = action.payload as string;
      })

      // Handle selectAllConversationsAsync
      .addCase(selectAllConversationsAsync.pending, (state) => {
        state.bulkActionStatus = 'loading';
      })
      .addCase(selectAllConversationsAsync.fulfilled, (state, action) => {
        const { idsToSelect } = action.payload;
        state.selectedConversationIds = idsToSelect;
        state.isSelectionMode = true;
        state.bulkActionStatus = 'idle';
      })
      .addCase(selectAllConversationsAsync.rejected, (state, action) => {
        state.bulkActionStatus = 'failed';
        state.bulkActionError = action.payload as string;
      });
  }
});

// Export actions
export const {
  setSelectedConversation,
  clearSelectedConversation,
  setFilter,
  toggleFilter,
  clearFilters,
  setCurrentPage,
  resetPagination,
  updateLastMessage,
  // Bulk selection actions
  toggleSelectionMode,
  setSelectionMode,
  toggleConversationSelection,
  selectConversations,
  selectAllConversations: selectAllConversationsAction,
  clearConversationSelection,
  clearBulkActionError
} = conversationsSlice.actions;

// Export selectors
export const selectAllConversations = (state: RootState) => state.whatsapp.conversations.groups;
export const selectSelectedConversation = (state: RootState) => state.whatsapp.conversations.selectedConversation;
export const selectConversationsStatus = (state: RootState) => state.whatsapp.conversations.status;
export const selectTotalConversations = (state: RootState) => state.whatsapp.conversations.totalConversations;
export const selectCurrentPage = (state: RootState) => state.whatsapp.conversations.currentPage;
export const selectFilters = (state: RootState) => state.whatsapp.conversations.filters;
// Bulk selection selectors
export const selectSelectedConversationIds = (state: RootState) => state.whatsapp.conversations.selectedConversationIds;
export const selectIsSelectionMode = (state: RootState) => state.whatsapp.conversations.isSelectionMode;
export const selectBulkActionStatus = (state: RootState) => state.whatsapp.conversations.bulkActionStatus;
export const selectBulkActionError = (state: RootState) => state.whatsapp.conversations.bulkActionError;
export const selectSelectedCount = (state: RootState) => state.whatsapp.conversations.selectedConversationIds.length;

// Export reducer
export default conversationsSlice.reducer;